import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { map, tap, finalize, filter, Subscription } from 'rxjs';
import { DropdownModule } from 'primeng/dropdown';
import { Currency, CurrencyRate, EditCurrencyResult } from 'src/app/shared/currency';
import { currencySaveErrorText } from 'src/app/shared/currency-save-error';
import { RestApiService } from 'src/app/services/rest-api.service';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { environment } from '../../../environments/environment';

/**
 * Reusable currency dropdown selector backed by `RestApiService`.
 *
 * On init (and on `selectedID` or `_currencyDate` input change) the component fetches the currency
 * list with rates for the given date and resolves the currently selected currency from `selectedID`.
 *
 * Two internal reload paths exist intentionally:
 * - `_reloadComponentData` — reloads the list and re-selects without emitting (used on `selectedID`
 *   change and on `ngOnInit`).
 * - `_reloadComponentDataToo` — reloads and **also emits `SelectedCurrency`** after load (used when
 *   `_currencyDate` changes, because the rate value changes and callers need the updated object).
 *
 * Optional action buttons (add currency, add rate, edit currency) are controlled individually via
 * `showAddCurrencyButton`, `showAddRateButton`, and `showEditButton` inputs.
 *
 */
@Component({
  selector: 'app-currencyselector',
  templateUrl: './currencyselector.component.html',
  styleUrls: ['./currencyselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class CurrencyselectorComponent implements OnInit, OnChanges, OnDestroy {

  _currList: Currency[] = null;

  /** Currently resolved `Currency` object matching `selectedID`; set internally by `_selectCurrency`. */
  @Input() _selectedCurrency: Currency;

  /** Reference date for fetching currency exchange rates. Defaults to today's date. */
  @Input() _currencyDate: Date;

  @ViewChild('CurrencySelectorDd') CurrencySelector: DropdownModule;

  /** Label text shown above the dropdown. */
  @Input() labelKey = 'Валюта';
  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder = 'Выбрать валюту...';

  /** When `true`, the Edit Currency button is rendered. */
  @Input() showEditButton: boolean = true;;
  /** When `true`, the Add Rate button is rendered. */
  @Input() showAddRateButton: boolean = true;;
  /** When `true`, the Add Currency button is rendered. */
  @Input() showAddCurrencyButton: boolean = true;;

  public get doShowEditButton(): boolean { return this.showEditButton; };
  public get doShowAddRateButton(): boolean { return this.showAddRateButton; };
  public get doShowAddCurrencyButton(): boolean { return this.showAddCurrencyButton; };

  /**
   * The ID of the selected currency (`Currency.CURID`).
   * Setting this triggers a list reload and re-resolution of `_selectedCurrency`.
   */
  @Input()
  get selectedID(): number { /*console.log('*** CurrencyselectorComponent selectedID get >>>: ' + this._selectedID);*/ return this._selectedID; }
  set selectedID(id: number) {
    // console.log('*** CurrencyselectorComponent selectedID set <<<: ' + id);
    this._selectedID = id;
    // console.log('*** CurrencyselectorComponent selectedID become : ' + this._selectedID);
    this._selectCurrency();
  }
  private _selectedID: number = null;

  /** When `true`, the dropdown is rendered as read-only. */
  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(b: boolean) {
    this._disabled = b;
  }
  _disabled: boolean = false;

  /** When `false`, the label above the dropdown is hidden. */
  @Input()
  get showLabel(): boolean { return this._showLabel; }
  set showLabel(b: boolean) {
    this._showLabel = b;
  }
  _showLabel: boolean = true;

  /** When `true`, the current exchange rate is displayed alongside the selected currency. */
  @Input() showRate: boolean = false;

  /** Emits the full `Currency` object (with rate for `_currencyDate`) when the user makes a selection. */
  @Output() public SelectedCurrency: EventEmitter<Currency> = new EventEmitter();

  /** Emits the selected currency's `CURID` when the user makes a selection. */
  @Output() public SelectedCurrencyID: EventEmitter<number> = new EventEmitter();

  private _getCurrencyQuery: Subscription;
  private _selCurrencyQuery: Subscription;
  private _saveQuery: Subscription;
  private _reloadSeq: number = 0;
  private _afterLoadCallbacks: Array<() => void> = [];

  /** `true` if a currency is currently selected (`selectedID` is non-null). */
  public get isThereSelection(): boolean { return (this._selectedID ? true : false) };

  /**
   * `true` when the caller holds `portal-admin`, `can-manage-currency`, or
   * `finance-manager` -- the three roles allowed to create/edit currencies
   * and exchange rates (2026-09-14, owner finding: the edit/add-rate/
   * add-currency buttons were visible *and enabled* for every user
   * regardless of role, since this selector is used on nearly every
   * document screen). Gates `[disabled]` on all three action buttons, not
   * `*ngIf` -- a viewer without one of these roles should still see the
   * controls exist, just not be able to use them, same as every other
   * role-gated control in this app.
   */
  public get canManageCurrency(): boolean {
    return this.globalsettingsservice.hasSomeRoles('portal-admin', 'can-manage-currency', 'finance-manager');
  }

  dlgHeader: string;
  displayNC: boolean = false;

  /** Failure text from the last save, shown inside the dialog (which stays open on failure). */
  saveError: string = '';

  /** True while a save request is in flight: the dialog is locked (see `EditCurrencyDlgComponent.saving`) and the open handlers do nothing. */
  saving: boolean = false;
  newCurrency: CurrencyRate = { CURID: 0, CURRRATEDATE: null, CURRRATEVALUE: 0 };

  constructor(
    private restapi: RestApiService,
    private globalsettingsservice: GetglobalsettingsService
  ) { this._currencyDate = new Date(); }

  /** Resolves `_selectedCurrency` from `_currList` based on the current `_selectedID`. */
  private _selectCurrency(): void {

    //console.log('*** CurrencyselectorComponent _selectCurrency _selectedID: ' + JSON.stringify(this._selectedID));
    //console.log('*** CurrencyselectorComponent _selectCurrency _currList  : ' + JSON.stringify(this._currList));

    this._selectedCurrency = (this._selectedID && this._currList) ? this._currList.find(x => x.CURID == this._selectedID) : null;

    //console.log('*** CurrencyselectorComponent _selectedCurrency          : ' + JSON.stringify(this._selectedCurrency));
    // this.emitCurrency();
  }

  /** Reloads the currency list (with rates) for `_currencyDate`, then re-resolves `_selectedCurrency`. Does not emit. */
  /**
   * Reloads the currency list. Only the most recently started reload may apply its result: an older request still
   * in flight is cancelled and its late response ignored, so it can neither overwrite a fresher list nor run the
   * `afterLoad` callbacks against a list that does not contain what was just saved. Callbacks of a superseded reload
   * are not lost -- they run when the latest reload finishes (`_afterLoadCallbacks`).
   */
  private _reloadComponentData(afterLoad?: () => void): void {
    //console.log('*** CurrencyselectorComponent _reloadComponentData: ' + JSON.stringify(this._currencyDate));
    const seq = ++this._reloadSeq;
    if (afterLoad) { this._afterLoadCallbacks.push(afterLoad); }
    if (this._getCurrencyQuery) { this._getCurrencyQuery.unsubscribe(); }

    this._getCurrencyQuery =
      this.restapi.getCurrencyWithRate(this._currencyDate).pipe(
        map(data => {
          if (seq === this._reloadSeq) { this._currList = data.sort((a, b) => (a.CURCODE >= b.CURCODE) ? 1 : -1); }
          return data;
        }),

        finalize(() => {
          if (seq !== this._reloadSeq) { return; }       // superseded (finalize also runs on unsubscribe)
          this._selectCurrency();
          this._afterLoadCallbacks.splice(0).forEach(cb => cb());
        })
      )
      .subscribe();
  }

  /**
   * Reloads the currency list for `_currencyDate`, re-resolves `_selectedCurrency`, then calls
   * `emitCurrency()`. Used when the date changes, because the rate value may differ.
   */
  private _reloadComponentDataToo(): void {
    //console.log('*** CurrencyselectorComponent _reloadComponentData: ' + JSON.stringify(this._currencyDate));
    this._getCurrencyQuery =
      this.restapi.getCurrency(this._currencyDate).pipe(
        map(data => this._currList = data.sort((a, b) => (a.CURCODE >= b.CURCODE) ? 1 : -1)),

        finalize(() => {
          this._selectCurrency();
          this.emitCurrency();
        })
      )
      .subscribe();
  }

  /** Reacts to `selectedID` and `_currencyDate` input changes and triggers the appropriate reload path. */
  ngOnChanges(changes: SimpleChanges) {
     //console.log('*** CurrencyselectorComponent ngOnChanges');
    for (let propName in changes) {
      let change = changes[propName];

      environment.production || console.log('*** CurrencyselectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
      //console.log('*** CurrencyselectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'selectedID':
          this._selectedID = change.currentValue;
          this._reloadComponentData();
          break;

        case '_currencyDate':
          this._reloadComponentDataToo();
          break;

      }
    }
  }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this._getCurrencyQuery) { this._getCurrencyQuery.unsubscribe();}
    if (this._selCurrencyQuery) { this._selCurrencyQuery.unsubscribe();}
    if (this._saveQuery) { this._saveQuery.unsubscribe(); }
  }

  /** Performs the initial currency list load. */
  ngOnInit(): void {
    console.log('*** CurrencyselectorComponent ngOnInit: ');
    this._reloadComponentData();
  }

  /** Emits `SelectedCurrencyID` with the current `_selectedID`. */
  emitCurrencyId() {
    if (this._selectedID) {
    }
    console.log('*** CurrencyselectorComponent emitCurrencyId in: ' + JSON.stringify(this._selectedID));
    this.SelectedCurrencyID.emit(this._selectedID);
  }

  /** Fetches full `Currency` info for `_selectedID` at `_currencyDate` and emits `SelectedCurrency`. No-op if nothing is selected. */
  emitCurrency() {
    if (this._selectedID) {
      console.log('*** CurrencyselectorComponent emitCurrency in: ' + JSON.stringify(this._selectedID));
      this._selCurrencyQuery =
        this.restapi.getCurrencyInfoByID(this._selectedID, this._currencyDate).pipe(

          tap(data => environment.production || console.log('*** CurrencyselectorComponent emitCurrency emitted 0: ' + JSON.stringify(data))),
          filter(data => data.length > 0),
          tap(data => environment.production || console.log('*** CurrencyselectorComponent emitCurrency emitted 1: ' + JSON.stringify(data[0]))),
          map(data => this.SelectedCurrency.emit(data[0]))
        )
          .subscribe();
    }
  }

  /** Handles PrimeNG dropdown `(onClick)` — syncs `_selectedID` from `_selectedCurrency` and emits both outputs. */
  onClickCS(e) {
    console.log('*** CurrencyselectorComponent onClickCS: ' + JSON.stringify(e))

    if (this._selectedCurrency) {

      this._selectedID = this._selectedCurrency.CURID;
      this.emitCurrency();
      this.emitCurrencyId();
    }
  }

  /** Handles PrimeNG dropdown `(onChange)` — syncs `_selectedID` from `_selectedCurrency` and emits both outputs. */
  onChangeCS(e) {
    console.log('*** CurrencyselectorComponent onChangeCS: ' + JSON.stringify(e));

    if (this._selectedCurrency) {

      this._selectedID = this._selectedCurrency.CURID;
      this.emitCurrency();
      this.emitCurrencyId();
    }
  }

  /** Opens the currency dialog in "add new currency" mode. */
  onCurrencyAddButton(e) {
    console.log('*** CurrencyselectorComponent onCurrencyAddButton: ' + JSON.stringify(e));
    if (this.saving) { return; }
    this.dlgHeader = 'Новая валюта';
    this.saveError = '';
    this.newCurrency.CURID = null;
    this.newCurrency.CURRRATEDATE = new Date();
    this.newCurrency.CURRRATEVALUE = 0.0;

    this.displayNC = true;
  }

  /** Opens the currency dialog in "add rate for selected currency" mode. */
  onRateAddButton(e) {
    console.log('*** CurrencyselectorComponent onRateAddButton: ' + JSON.stringify(e));
    if (this.saving) { return; }

    // A null CURID would put the dialog in new-currency mode and create a currency from an "add rate" click.
    if (!this._selectedID) { return; }

    this.dlgHeader = 'Добавить курс';
    this.saveError = '';
    this.newCurrency.CURID = this._selectedID;
    this.newCurrency.CURRRATEDATE = new Date();
    this.newCurrency.CURRRATEVALUE = 0.0;

    this.displayNC = true;
  }

  /** Opens the currency dialog in "edit selected currency" mode. */
  onCategoryEditButton(e) {
    console.log('*** CurrencyselectorComponent onCategoryEditButton: ' + JSON.stringify(e));
    if (this.saving) { return; }

    this.dlgHeader = 'Редактирование валюты';
    this.saveError = '';
    this.newCurrency.CURID = this._selectedID;
    this.newCurrency.CURRRATEDATE = new Date();
    this.newCurrency.CURRRATEVALUE = 0.0;

    this.displayNC = true;
  }

  /**
   * Saves what the inner currency dialog confirmed through `RestApiService.saveCurrencyEdit` (a new currency with
   * its first rate, a rename of an existing one via `PUT /Currency/:id`, and/or a new dated rate), then reloads the list
   * and re-emits the selection if the selected currency changed. The dialog stays open on failure and shows `saveError`.
   */
  onNewCurrencySelect(e: EditCurrencyResult) {
    if (!e || this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    // `e.CURID` null -> the API creates the currency from `CURCODE`/`CURNAME` together with this rate.
    this._saveQuery = this.restapi.saveCurrencyEdit(e)
      .pipe(finalize(() => this.saving = false))
      .subscribe({
        next: created => {
          this.displayNC = false;

          if (e.selectAfter) { this._selectedID = created.CURID; }

          this._reloadComponentData(() => {
            if (e.selectAfter) {
              this.emitCurrency();
              this.emitCurrencyId();
            } else if (created.CURID == this._selectedID) {
              // The selected currency got a new rate -- tell the host so it re-reads it.
              this.emitCurrency();
            }
          });
        },
        error: (err: unknown) => { this.saveError = currencySaveErrorText(err); }
      });
  }

  onCurrencyDialogClose(event) {
    this.displayNC = event;
  }
}
